'use client';

/**
 * API namespace `welcomeMessage`.
 * Verbatim extraction from frontend/src/lib/api-client.ts (L9316-L9444).
 *
 * Part of SPEC-244 IA17 split (refactor/spec244-ia17-api-client).
 * Transformations: T-DEDENT-2, T-EXPORT, T-IMPORTS (see ledger).
 */

import { apiFetch } from '../core/fetch-client';

import type {
  AuthResponse, ApiError, RegisterRequest, LoginRequest, PublicUser,
  DashboardData, FeatureFlags, UpdateProfileInput, ChangePasswordInput,
  Setup2FAResult, Verify2FAResult, Login2FAResponse,
  ForgotPasswordInput, ResetPasswordInput, VerifyEmailInput,
  NetworkTree, WalletData, ConferencesData, CreateConferenceInput,
  CreateInterviewBody, InterviewCreatedResponse, InterviewListResponse,
  InviteBody, InviteResponse, JoinBody, JoinResponse,
  DownlineResult, QualificationResult, IDeposit, PayoutResult,
  ReconciliationResult,
  UserDepositStatus,
  CreditBalanceResponse, CreditHistoryResponse,
  CreditPurchaseRequest, CreditPurchaseResponse, CreditTransactionsResponse,
  SellerRegistrationRequest, SellerProfileResponse,
  FulfillmentConfigRequest, OnboardingStepRequest,
  SellerDashboardResponse, PublicStoreResponse,
  StoreBranding, StoreBrandingUpdate,
  SellerPayoutRequestBody, SellerPayoutBalanceResponse,
  ProductCreateRequest, ProductUpdateRequest,
  SellerProductResponse, ProductStatus,
  AdminProductListItem, AdminProductListResponse,
  ProductListResponse, PublicProductResponse, ProductListFilters,
  OrderCreateRequest, OrderCreateRequestUnion,
  OrderResponse, OrderListResponse, ShipOrderRequest,
  ProductBundle, ProductBundleResponse, ProductBundleListResponse,
  ProductBundleStatus, ProductBundleCreateInput, ProductBundleUpdateInput,
  Coupon, CouponResponse, CouponListResponse, CouponStatus,
  CouponPurchaseResponse, CouponPurchaseListResponse,
  CouponCreateInput, CouponUpdateInput, CouponPurchaseInput,
  GiftCard, GiftCardResponse, GiftCardListResponse, GiftCardStatus,
  GiftCardPurchaseResponse, GiftCardPurchaseListResponse,
  GiftCardCreateInput, GiftCardUpdateInput, GiftCardPurchaseInput,
  RedemptionMethod, PurchaseStatus,
  Dispute, DisputeStatus, DisputeChannel,
  Evidence, EvidenceType, DisputeMessage,
  Review, ReviewDirection, ReviewRating,
  ReviewListItem, ReviewListResponse,
  NetworkStats, WalletTransaction, WalletStats,
  RoomParticipantStat, ConferenceStats, NetworkNode,
  AnnouncementDto, CreateAnnouncementBody, UpdateAnnouncementBody,
  KycApplicationDto, Paginated,
  DepositDto, DepositStatus,
  DepositNewPayload, PayoutDto, PayoutStatus, PayoutStatusChangePayload,
  AuditListQuery, PaginatedAuditEntries, AuditChainVerificationResult,
  KycApplication, KycReviewStatus, KycDocumentType,
  KycFatfInput, GeoLocationOption, ZodType, Landing,
  AccountingNetwork, AccountingNetworkBreakdown, AccountingSummary,
  PayoutRequestBody, PushSubscribeInput, PushUnsubscribeInput,
  AcademyTrack, AcademyCourseStatus, AcademyCourseDifficulty,
  AcademyCourse, AcademyCourseWithMeta, AcademyCatalogResponse,
  AcademyModuleListItem, AcademyModuleDetail, AcademyModuleResource,
  AcademyEnrollmentSummary, AcademyMyCourse, AcademyMyCertificate,
  AcademyVerifyResult, AcademyMarkProgressResponse, AcademyAdminCourse,
  IUserCertification, EntrepreneurProfile, EntrepreneurProfileInput,
  DiscoveryResponse,
  AccountingUserBalances, AccountingLedgerEntry,
  AccountBalanceResult, TrialBalanceRow, TrialBalanceResult,
  ReconciliationReport, ReconciliationStatusResponse,
  ReconciliationHistoryResponse, AccountingPostResult,
  UnsignedTransaction, SubmitTransactionResult,
  TransactionStatusResult, PrepareTransactionBody,
  SubmitTransactionBody, AwaitingSignaturePayout,
  WelcomeMessageResponse, WelcomeMessageInput,
  SavedAddressResponse, SavedAddressInput,
  ForbiddenWordResponse, ForbiddenWordInput,
  PublicRoomInfo, PublicRoomBySlugInfo,
} from '../core/shared-types';

import type {
  AdminUserDto, AdminUserDetailDto, AdminPurgeUserResultDto,
  FeatureFlagDto, AdminConferenceDto, LandingLimits, AdminMetricsDto,
  DepositChainInfo, UserDepositDto,
  CircuitState, CircuitBreakerMetricsDto,
  CircuitBreakerResetResultDto, CircuitBreakerStateChangePayload,
  TransactionLedgerEntryDto, TransactionDto,
  DisputeResponse, DisputeListResponse, DisputeMessageResponse,
  EvidenceUploadResponse, AppealResponse, ReviewResponse,
  BundleStats, CouponStats, GiftCardStats,
  AdminDisputeListFilters, AdminDisputeListResponse,
  AdminDisputeDetailResponse, AdminResolveDisputeBody,
  AdminResolveAppealBody, AdminModerateReviewBody,
  ReconciliationStatus,
} from '../core/types';

import type {
  ConferenceRoomResponse, InterviewRoomResponse,
  ReservationAvailabilitySlot, ReservationAvailability,
  ReservationSlot, BookingInput, ReservationCreated, ReservationListItem,
  DirectMessageDTO, SendDirectMessageInput, UploadedDirectFileDTO,
} from '../core/interfaces';

export const welcomeMessage = {
  /**
   * GET /api/welcome-message — get my own networker welcome message.
   *
   * Returns `{ data: WelcomeMessageResponse | null }`. `null` when the
   * user has not yet created a welcome message.
   *
   * Sprint 138 PR-E: now accepts `creatorType` param ('networker' |
   * 'seller') to support the seller welcome message editor. Default
   * 'networker' for back-compat.
   */
  async getMine(
    opts?: { creatorType?: 'networker' | 'seller'; signal?: AbortSignal },
  ): Promise<WelcomeMessageResponse | null> {
    const qs = opts?.creatorType ? `?creatorType=${opts.creatorType}` : '';
    return apiFetch<WelcomeMessageResponse | null>(`/welcome-message${qs}`, {
      signal: opts?.signal,
    });
  },
  /**
   * POST /api/welcome-message — create or replace (upsert) my own
   * networker welcome message.
   *
   * If a welcome message already exists for this creator (networker),
   * the backend replaces it in place (INV-WELCOME-05 — the creator can
   * edit at any time; changes apply to FUTURE sends, not to messages
   * already sent — those are immutable due to DirectMessage TTL).
   *
   * Requires recent 2FA. If the backend returns 403 with code
   * `2FA_REQUIRED`, the caller should open the 2FA prompt and retry.
   *
   * Sprint 138 PR-E: now accepts `creatorType` param ('networker' |
   * 'seller') to support the seller welcome message editor. Default
   * 'networker' for back-compat.
   */
  async save(
    input: WelcomeMessageInput,
    opts?: { creatorType?: 'networker' | 'seller' },
  ): Promise<WelcomeMessageResponse> {
    const qs = opts?.creatorType ? `?creatorType=${opts.creatorType}` : '';
    return apiFetch<WelcomeMessageResponse>(`/welcome-message${qs}`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  /**
   * DELETE /api/welcome-message — soft-delete my own welcome message
   * (status='inactive' per INV-WELCOME-05).
   *
   * Requires recent 2FA (same as `save`).
   *
   * Sprint 138 PR-E: now accepts `creatorType` param ('networker' |
   * 'seller') to support the seller welcome message editor. Default
   * 'networker' for back-compat with the existing /app/welcome-message
   * page (Feature 1.A).
   */
  async delete(opts?: { creatorType?: 'networker' | 'seller' }): Promise<{ status: 'inactive' }> {
    const qs = opts?.creatorType ? `?creatorType=${opts.creatorType}` : '';
    return apiFetch<{ status: 'inactive' }>(`/welcome-message${qs}`, {
      method: 'DELETE',
    });
  },
  /**
   * Sprint 138 PR-E (Feature 1.B — Seller Welcome, ADR-058 §Feature 1
   * APROBADO por Founder 2026-09-25): trigger the seller's welcome
   * message to be sent to the authenticated comprador. Idempotente —
   * the backend returns { sent: false, reason: 'already_sent' } if the
   * par (comprador, seller) already received a welcome from this seller.
   *
   * Called from 2 trigger points in the frontend:
   *   - Trigger (a) "visita tienda por primera vez" — StoreVisitTracker
   *     client component in /market/tienda/[storeSlug]/page.tsx fires
   *     on mount if the visitor is authenticated. Passes { storeSlug }.
   *   - Trigger (b) "primera compra al seller" — useEffect in
   *     /market/ordenes/[orderNumber]/page.tsx fires when
   *     order.status === 'paid' || 'delivered'. Passes { sellerId }
   *     (the Seller.sellerId UUID exposed in the order response).
   *
   * Privacy: accepts the Seller's public identifiers (storeSlug OR
   * sellerId UUID) — NOT User._id (private). The backend resolves either
   * to seller.userId internally via Seller.findOne.
   *
   * Returns:
   *   - { sent: true } when a new DirectMessage was created (first time)
   *   - { sent: false, reason: 'already_sent' } when idempotencia skips
   *   - { sent: false, reason: 'no_active_welcome' } when seller has no
   *     active WelcomeMessage of creatorType='seller'
   *   - { sent: false, reason: 'seller_not_found' } when no seller with
   *     the given storeSlug/sellerId
   *   - { sent: false, reason: 'internal_error' } when DB error occurs
   *     (fail-open — no exception thrown, the caller doesn't crash)
   *
   * The frontend should NOT show error toasts for any reason value —
   * the welcome message is best-effort, not a critical feature for
   * the visita/compra flow. Silent failure is correct UX.
   */
  async triggerSeller(
    identifier: { storeSlug?: string; sellerId?: string },
    triggerType: 'store_visit' | 'first_purchase',
  ): Promise<{ sent: boolean; reason?: 'already_sent' | 'no_active_welcome' | 'seller_not_found' | 'internal_error' }> {
    return apiFetch<{ sent: boolean; reason?: 'already_sent' | 'no_active_welcome' | 'seller_not_found' | 'internal_error' }>(
      '/welcome-message/seller/trigger',
      {
        method: 'POST',
        body: JSON.stringify({ ...identifier, triggerType }),
      },
    );
  },
};
